import React, { PropsWithChildren, useMemo } from "react";
import {
  useShareTradeResults,
  useTradesBulkUpdate,
} from "@shared/features/trades/common/hooks";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { displayResultMessage, openMessage } from "utils/misc";
import MoveToCurrentPriceModal from "features/trades/OpenTrades/MoveToCurrentPriceModal";
import SellPartiallyModal from "features/trades/OpenTrades/SellPartiallyModal";
import BulkCloseTradesModal from "features/trades/CancelTrade/BulkCloseTradesModal";
import CancelTradeModal from "features/trades/CancelTrade/CancelTradeModal";
import ShareImageModal from "Global/ShareImageModal";
import useTradeActionsProvider from "@shared/features/trades/common/useTradeActionsProvider";
import useQuotaModal from "features/settings/Quota/useQuotaModal";

const TradeActionsProvider = ({ children }: PropsWithChildren) => {
  const {
    isShareResultsModalVisible,
    shareTradeImageUrl,
    shareTradeImageLoading,
    openShareResultsModal,
    closeShareResultsModal,
    imageTitle,
    getShareResultsErrorMessage,
    shareTradeImageArgs,
  } = useShareTradeResults(displayResultMessage, openMessage);
  const {
    isMoveEntryModalVisible,
    isSellPartiallyModalVisible,
    isCancelTradeModalVisible,
    modalTradeData,
    closeMoveEntryModal,
    closeSellPartiallyModal,
    closeCancelTradeModal,
    getMoveEntryErrorMessage,
    getSellPartiallyErrorMessage,
    getEditTradeErrorMessage,
    handleTradeActionClick,
    quotaModal,
    getIsTradeReadOnlyAccount,
  } = useTradeActionsProvider({
    openMessage,
    useQuotaModal,
    openShareResultsModal,
  });
  const tradesBulkUpdateData = useTradesBulkUpdate(
    handleTradeActionClick,
    openMessage
  );
  const {
    bulkCloseTradesModalOpen,
    closeBulkCloseTradesModal,
    onFinishBulkCloseTrades,
    selectedTradesIDs,
    tradeIDToTradeData,
  } = tradesBulkUpdateData;

  const contextValue = useMemo(() => {
    return {
      getMoveEntryErrorMessage,
      getSellPartiallyErrorMessage,
      getEditTradeErrorMessage,
      shareTradeImageLoading,
      shareTradeImageArgs,
      getShareResultsErrorMessage,
      handleTradeActionClick,
      getIsTradeReadOnlyAccount,
      ...tradesBulkUpdateData,
    };
  }, [
    getMoveEntryErrorMessage,
    getSellPartiallyErrorMessage,
    getEditTradeErrorMessage,
    shareTradeImageLoading,
    shareTradeImageArgs,
    handleTradeActionClick,
    tradesBulkUpdateData,
  ]);

  return (
    <TradeActionsContext.Provider value={contextValue}>
      {children}
      <MoveToCurrentPriceModal
        closeModal={closeMoveEntryModal}
        tradeData={modalTradeData}
        isVisible={isMoveEntryModalVisible}
      />
      <SellPartiallyModal
        closeModal={closeSellPartiallyModal}
        tradeData={modalTradeData}
        isVisible={isSellPartiallyModalVisible}
      />
      <CancelTradeModal
        tradeData={modalTradeData}
        isVisible={isCancelTradeModalVisible}
        closeModal={closeCancelTradeModal}
        symbol={modalTradeData?.symbol}
        accountId={modalTradeData?.client_id}
      />
      <BulkCloseTradesModal
        open={bulkCloseTradesModalOpen}
        closeModal={closeBulkCloseTradesModal}
        tradeIDs={selectedTradesIDs}
        tradeIDToTradeData={tradeIDToTradeData}
        onFinish={onFinishBulkCloseTrades}
      />
      <ShareImageModal
        visible={isShareResultsModalVisible}
        closeModal={closeShareResultsModal}
        imageUrl={shareTradeImageUrl}
        imageTitle={imageTitle}
      />
      {quotaModal}
    </TradeActionsContext.Provider>
  );
};

export default TradeActionsProvider;
